import PropTypes from "prop-types"
import React, { useState } from "react"

// Icons for the search bar
const TimeIcon = ({ theme }) => (
  <svg className="h-8 w-8" viewBox="0 0 32 32" fill="none">
    <path
      d="M16 4C9.373 4 4 9.373 4 16s5.373 12 12 12 12-5.373 12-12S22.627 4 16 4zm0 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <path d="M16 8v8l6 6" stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"} strokeWidth="2" fill="none" />
  </svg>
)

const MapPinIcon = ({ theme }) => (
  <svg className="h-8 w-8" viewBox="0 0 32 32" fill="none">
    <path
      d="M27 13.333C27 22 16 30 16 30S5 22 5 13.333C5 8.913 9.477 5.333 16 5.333s11 3.58 11 8z"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <circle
      cx="16"
      cy="13.333"
      r="3"
      stroke={theme?.colors?.primary || "rgba(189, 101, 79, 1)"}
      strokeWidth="2"
      fill="none"
    />
  </svg>
)

const ChevronDownIcon = ({ theme }) => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 9l6 6 6-6"
      stroke={theme?.colors?.textDark || "#55433f"}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ServiceTimeSearchBar = ({
  service,
  selectedDuration,
  selectedLocation,
  onDurationChange,
  onLocationChange,
  hasMultipleDurations,
  hasMultipleLocations,
  theme
}) => {
  const [showDurationMenu, setShowDurationMenu] = useState(false)
  const [showLocationMenu, setShowLocationMenu] = useState(false)

  const formatPrice = (amountCents) => {
    if (!amountCents) return ""
    return `$${(amountCents / 100).toFixed(0)}`
  }

  const formatDuration = (timeLength) => {
    if (!timeLength) return ""
    return `${timeLength} min`
  }

  const formatLocation = (location) => {
    if (!location) return "Select location"

    if (location.kind === "virtual") {
      return "Virtual"
    } else if (location.kind === "home") {
      return "At your home"
    } else if (location.kind === "office") {
      return "In office"
    } else {
      return location.address || `${location.city}, ${location.stateAbbreviation}`
    }
  }

  const handleDurationSelect = (duration) => {
    onDurationChange(duration)
    setShowDurationMenu(false)
  }

  const handleLocationSelect = (location) => {
    onLocationChange(location)
    setShowLocationMenu(false)
  }

  // Get duration options (main service + variations)
  const durationOptions = [
    {
      id: service.id,
      name: service.name,
      amountCents: service.amountCents,
      timeLength: service.timeLength
    },
    ...(service.variations || [])
  ]

  return (
    <div
      className="box-border flex h-32 w-[872px] items-center justify-start gap-4 rounded-xl px-8 py-6 shadow-[0px_8px_9px_0px_rgba(0,0,0,0.07)]"
      style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
      {/* Duration Selector */}
      <div className="relative">
        <div
          className="box-border flex w-[214px] cursor-pointer items-center justify-start gap-2 rounded-xl border px-5 py-3"
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}
          onClick={() => hasMultipleDurations && setShowDurationMenu(!showDurationMenu)}>
          <div className="flex flex-1 items-center justify-start gap-4">
            <TimeIcon theme={theme} />
            <div
              className="flex flex-1 flex-col items-start justify-start"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className="w-full text-[20px] font-semibold leading-[32px]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {selectedDuration ? formatDuration(selectedDuration.timeLength) : "60 min"}
              </div>
              <div
                className="w-full text-[16px] font-normal leading-[24px]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {selectedDuration ? formatPrice(selectedDuration.amountCents) : formatPrice(service.amountCents)}
              </div>
            </div>
          </div>
          {hasMultipleDurations && <ChevronDownIcon theme={theme} />}
        </div>

        {/* Duration Dropdown */}
        {showDurationMenu && hasMultipleDurations && (
          <div
            className="absolute left-0 top-full z-10 mt-1 w-[214px] rounded-lg border px-2 py-4 shadow-lg"
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            {durationOptions.map((duration) => (
              <div
                key={duration.id}
                className={`flex cursor-pointer items-center justify-start gap-4 rounded px-4 py-2 ${
                  selectedDuration?.id === duration.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"
                }`}
                onClick={() => handleDurationSelect(duration)}>
                <TimeIcon theme={theme} />
                <div
                  className="flex flex-1 flex-col items-start justify-start"
                  style={{ color: theme?.colors?.textDark || "#55433f" }}>
                  <div
                    className="text-[16px] font-semibold leading-[24px]"
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {formatDuration(duration.timeLength)}
                  </div>
                  <div
                    className="text-[14px] font-normal leading-[20px]"
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {formatPrice(duration.amountCents)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Selector */}
      <div className="relative flex-1">
        <div
          className="box-border flex h-full cursor-pointer items-center justify-start gap-2 rounded-xl border px-5 py-3"
          style={{
            borderColor: theme?.colors?.greyLight || "#e6e6e6",
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}
          onClick={() => hasMultipleLocations && setShowLocationMenu(!showLocationMenu)}>
          <div className="flex flex-1 items-center justify-start gap-4">
            <MapPinIcon theme={theme} />
            <div
              className="flex flex-1 flex-col items-start justify-start"
              style={{ color: theme?.colors?.textDark || "#55433f" }}>
              <div
                className="w-full text-[20px] font-semibold leading-[32px]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                {formatLocation(selectedLocation)}
              </div>
              <div
                className="w-full text-[16px] font-normal leading-[24px]"
                style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                Location
              </div>
            </div>
          </div>
          {hasMultipleLocations && <ChevronDownIcon theme={theme} />}
        </div>

        {/* Location Dropdown */}
        {showLocationMenu && hasMultipleLocations && (
          <div
            className="absolute left-0 top-full z-10 mt-1 w-full rounded-lg border px-2 py-4 shadow-lg"
            style={{
              borderColor: theme?.colors?.greyLight || "#e6e6e6",
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            {service.locations.map((location) => (
              <div
                key={location.id}
                className={`flex cursor-pointer items-center justify-start gap-4 rounded px-4 py-2 ${
                  selectedLocation?.id === location.id ? "bg-[#f8f6f5]" : "hover:bg-[#f8f6f5]"
                }`}
                onClick={() => handleLocationSelect(location)}>
                <MapPinIcon theme={theme} />
                <div
                  className="flex flex-1 flex-col items-start justify-start"
                  style={{ color: theme?.colors?.textDark || "#55433f" }}>
                  <div
                    className="text-[16px] font-semibold leading-[24px]"
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {formatLocation(location)}
                  </div>
                  <div
                    className="text-[14px] font-normal leading-[20px]"
                    style={{ fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif" }}>
                    {location.kind === "virtual"
                      ? "Online"
                      : location.kind === "home"
                      ? "In-home"
                      : location.kind === "office"
                      ? "In-office"
                      : "In-person"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

ServiceTimeSearchBar.propTypes = {
  service: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    amountCents: PropTypes.number,
    timeLength: PropTypes.number,
    variations: PropTypes.arrayOf(PropTypes.object),
    locations: PropTypes.arrayOf(PropTypes.object)
  }).isRequired,
  selectedDuration: PropTypes.object,
  selectedLocation: PropTypes.object,
  onDurationChange: PropTypes.func.isRequired,
  onLocationChange: PropTypes.func.isRequired,
  hasMultipleDurations: PropTypes.bool.isRequired,
  hasMultipleLocations: PropTypes.bool.isRequired,
  theme: PropTypes.object
}

export default ServiceTimeSearchBar
